import { ComponentProps, useState } from 'react'
import { View, StyleSheet, Text } from 'react-native'
import { useRouter } from 'expo-router'
import { mesaColorNames, typography } from '@/src/common/theme'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { useLogError } from '@/src/common/hooks/useLogError'
import { BankOutlined } from '@/src/assets/icons/BankOutlined'
import { DesignSystem } from '@/src/design-system'
import ChevronWithIcon from '../components/ChevronWithIcon'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { GET_ACTIVE_MORTGAGE_LEADS } from '@/src/common/gql/queries/mortgage-lead'
import * as Mortgage from '@/src/experiences/mortgage'
import { Loading } from '@/src/common/components/Loading'
import * as Experiences from '@/src/experiences'

export const DEFAULT_SCREEN_VARIANT = 'default'

export function MortgageModule({ style }: Pick<ComponentProps<typeof DesignSystem.Card>, 'style'>) {
  const router = useRouter()
  const onError = useLogError()

  const [moduleVariant, setModuleVariant] =
    useState<Mortgage.Statsig.Store.MortgageModuleVariantName>(DEFAULT_SCREEN_VARIANT)
  const { loading: loadingUserData } = useLoggedInQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const sourcePartnerName = data.getUserInfo.sourcePartnerName?.toLowerCase()
      if (!sourcePartnerName) {
        setModuleVariant(DEFAULT_SCREEN_VARIANT)
      } else {
        try {
          const parsedSourcePartnerName =
            Mortgage.Statsig.Store.MortgageModuleVariantName.parse(sourcePartnerName)
          setModuleVariant(parsedSourcePartnerName)
        } catch (_) {
          setModuleVariant(DEFAULT_SCREEN_VARIANT)
        }
      }
    },
  })

  const { loading: loadingMortgageLeads, data: activeMortgageLeads } = useLoggedInQuery(
    GET_ACTIVE_MORTGAGE_LEADS,
    { onError },
  )

  const hasActiveMortgageLead =
    Array.isArray(activeMortgageLeads?.getMortgageLeads) &&
    activeMortgageLeads.getMortgageLeads.length > 0 &&
    !loadingMortgageLeads &&
    !loadingUserData

  const { title, description, action_text, image_url } =
    Mortgage.Statsig.Store.useMortgageModuleDesign(moduleVariant)

  return (
    <DesignSystem.Card
      style={[{ paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0, gap: 16 }, style]}
      key="mortgage-marketplace-module"
      testID={Mortgage.E2E.Config.Ids.View.MesaMortgage}
    >
      {loadingMortgageLeads || loadingUserData ? (
        <Loading />
      ) : (
        <>
          <Experiences.Mortgage.Components.Header imageUrl={image_url} />
          <View style={{ gap: 8, marginHorizontal: 16, flexGrow: 1 }}>
            <Text
              style={[
                typography['heading/heading3'],
                { color: mesaColorNames['Mesa - Brand/Secondary #223843'] },
              ]}
            >
              {title}
            </Text>
            <Text style={[typography.S, { color: mesaColorNames['Greyscale/900 #152438'] }]}>
              {description}
            </Text>
          </View>
          <View
            style={{
              height: StyleSheet.hairlineWidth,
              backgroundColor: mesaColorNames['Black/10% rgba(28, 28, 28, 0.1)'],
              marginHorizontal: 16,
            }}
          />
          <ChevronWithIcon
            containerStyle={{
              marginBottom: 16,
              marginHorizontal: 16,
              paddingVertical: 0,
              paddingHorizontal: 0,
            }}
            Icon={
              <BankOutlined
                pathProps={{
                  stroke: hasActiveMortgageLead
                    ? mesaColorNames['Mesa - Brand/White #FFFFFF']
                    : mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
                }}
                svgProps={{ width: 22, height: 22 }}
              />
            }
            onPress={() => router.push('/main/(tabs)/home/mortgage')}
            onIconEndPress={() => router.push('/main/(tabs)/home/mortgage')}
            text={action_text}
            caption={hasActiveMortgageLead ? 'Your inquiry has been received 📬' : 'Get Started '}
            segmentAction={'User Pressed the "Mortgage Section" card component'}
            iconContainerStyle={
              hasActiveMortgageLead && {
                backgroundColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
              }
            }
            testID={Mortgage.E2E.Config.Ids.Press.GetStarted}
          />
        </>
      )}
    </DesignSystem.Card>
  )
}
